import {defineStore} from 'pinia'
import Vue from 'vue'
import {isEmpty} from 'lodash'

export const usePhonesStore = defineStore('phones', {
    state: () => {
        return {
            configurations: [],
            configurationsInitialState: [],
            currentSelectedConfiguration: '',
            currentSelectedConfigurationIndex: -1,
            chosenModel: '',
            mdmDeviceIdsWithUnsavedChanges: []
        }
    },
    actions: {

        setConfigurations(configurations) {
            Vue.set(this, 'configurations', configurations)
        },
        /**
         * Returns an array of phone configuration objects.
         *
         * Each object represents a device profile paired with a set of configurations, apps, credentials, etc.
         *
         * @returns {Array<Object>} A list of devices, each with a unique key and detailed configuration.
         *
         * Structure of each item:
         * [
         *   {
         *     key: {String} - Display name or identifier of the phone (e.g. "Priyank's Realme 5 Pro edited")
         *     value: {
         *       phone_model: {String} - Model identifier of the phone
         *       user_group: {String} - Associated user group (empty if unassigned)
         *       user: {String} - User identifier (if assigned)
         *       device: {String} - Device name or identifier (optional)
         *
         *       userInfo: {
         *         login: {String} - User login email or username
         *         mdmDeviceId: {String} - Device ID used in MDM context
         *         password: {String} - MDM user password
         *       },
         *
         *       deviceInfo: {
         *         androidVersion: {String} - OS version
         *         apiLevel: {Number} - Android API level
         *         availableRAM: {Number} - Available RAM in bytes
         *         cpuInfo: {String} - CPU architecture
         *         deviceBrand: {String} - Brand name (e.g. "realme")
         *         deviceId: {String} - Unique device ID
         *         deviceManufacturer: {String} - Manufacturer name
         *         deviceModel: {String} - Device model name
         *         deviceProduct: {String} - Product name of the device
         *         hardware: {String} - Hardware descriptor (e.g. "qcom")
         *         isEmulator: {Boolean} - Whether device is an emulator
         *         osName: {String} - OS name (e.g. "Android 11")
         *         screenDensity: {Number} - Pixel density (dpi scale factor)
         *         screenHeight: {Number} - Screen height in pixels
         *         screenWidth: {Number} - Screen width in pixels
         *         totalRAM: {Number} - Total RAM in bytes
         *       },
         *
         *       applications: Array<{ appName: String, packageId: String }>
         *         - List of known applications (based on your controlled app store or config)
         *
         *       installed_applications: Array<{ appName: String, packageId: String }>
         *         - Actual apps found installed on the device (might include unknown or user-installed apps)
         *
         *       credentials: {
         *         server: {String} - MDM server address
         *         account: {String} - Account/username used
         *         password: {String} - Placeholder or actual password if applicable
         *       }
         *     }
         *   }
         * ]
         */
        getConfigurations() {
            return this.configurations
        },
        /**
         * Returns an array of phone configuration objects.
         *
         * Each object represents a device profile paired with a set of configurations, apps, credentials, etc.
         *
         * @returns {Array<Object>} A list of devices, each with a unique key and detailed configuration.
         *
         * Structure of each item:
         * [
         *   {
         *     key: {String} - Display name or identifier of the phone (e.g. "Priyank's Realme 5 Pro edited")
         *     value: {
         *       phone_model: {String} - Model identifier of the phone
         *       user_group: {String} - Associated user group (empty if unassigned)
         *       user: {String} - User identifier (if assigned)
         *       device: {String} - Device name or identifier (optional)
         *
         *       userInfo: {
         *         login: {String} - User login email or username
         *         mdmDeviceId: {String} - Device ID used in MDM context
         *         password: {String} - MDM user password
         *       },
         *
         *       deviceInfo: {
         *         androidVersion: {String} - OS version
         *         apiLevel: {Number} - Android API level
         *         availableRAM: {Number} - Available RAM in bytes
         *         cpuInfo: {String} - CPU architecture
         *         deviceBrand: {String} - Brand name (e.g. "realme")
         *         deviceId: {String} - Unique device ID
         *         deviceManufacturer: {String} - Manufacturer name
         *         deviceModel: {String} - Device model name
         *         deviceProduct: {String} - Product name of the device
         *         hardware: {String} - Hardware descriptor (e.g. "qcom")
         *         isEmulator: {Boolean} - Whether device is an emulator
         *         osName: {String} - OS name (e.g. "Android 11")
         *         screenDensity: {Number} - Pixel density (dpi scale factor)
         *         screenHeight: {Number} - Screen height in pixels
         *         screenWidth: {Number} - Screen width in pixels
         *         totalRAM: {Number} - Total RAM in bytes
         *       },
         *
         *       applications: Array<{ appName: String, packageId: String }>
         *         - List of known applications (based on your controlled app store or config)
         *
         *       installed_applications: Array<{ appName: String, packageId: String }>
         *         - Actual apps found installed on the device (might include unknown or user-installed apps)
         *
         *       credentials: {
         *         server: {String} - MDM server address
         *         account: {String} - Account/username used
         *         password: {String} - Placeholder or actual password if applicable
         *       }
         *     }
         *   }
         * ]
         */
        getConfigurationsInitialState() {

            if (typeof this.configurationsInitialState !== 'undefined' && !isEmpty(this.configurationsInitialState)) {
                return JSON.parse(this.configurationsInitialState)
            }

        },
        setConfigurationsInitialState(phones) {

            if (typeof phones === 'object') {
                this.configurationsInitialState = JSON.stringify(phones)
            }

        },

        getMdmDeviceIdsWithUnsavedChanges(){
            return this.mdmDeviceIdsWithUnsavedChanges
        },

        setMdmDeviceIdsWithUnsavedChanges(mdmDeviceIds) {
            this.mdmDeviceIdsWithUnsavedChanges = mdmDeviceIds
        }

    },
    getters: {}

})
